/**
 * AI Hint Provider Service
 * 
 * Generates visual hints for valid player actions based on game state.
 * Uses AI logic to determine best recommended action.
 */

import { GameState, GamePhase, PreparedCard, CardColor, CardType, Player } from '../types';
import { getBestAIAction, AIAction, runAIPreparationTurn, getAIPhaseAction } from './ai';
import { getCardEffect } from './effects';
import { getAvailablePrepareSlots, getEffectiveEssenceCostForPlayer } from './gameLogic';
import { canCounterblastTarget, isImmediateOrReflectedDamageSource, isSpikesCard } from './damageRules';

// ============================================================================
// TYPES
// ============================================================================

export type HintElementType =
    | 'PREPARED_CARD'      // Card in preparation area
    | 'HAND_CARD'          // Card in hand (for Mage spells)
    | 'TOWER'              // Tower/Crystal
    | 'BUTTON'             // Action button (Pass, End Day, Accept, etc.)
    | 'AVATAR'             // Hero avatar
    | 'SPELL'              // Mage spell in hand
    | 'SLOT';              // Empty preparation slot

export type HintPriority = 'AI_BEST' | 'VALID';

export interface ActionHint {
    elementId: string;              // Unique ID of the element
    elementType: HintElementType;   // Type of game element
    priority: HintPriority;         // AI_BEST = 6 waves, colored; VALID = 3 waves, gold
    cardColor?: CardColor;          // For colored highlighting (AI_BEST)
    validSides?: {                  // For dual-sided cards
        top: boolean;                 // Top effect is valid
        bottom: boolean;              // Bottom effect is valid (for typeB cards)
    };
    actionType?: string;            // Type of action (for debugging)
    bestActionRotation?: number;    // Expose the AI's desired rotation for this action
    secondaryElementId?: string;    // Second element to highlight (e.g. target of an attack)
}

export interface HintProviderResult {
    hints: ActionHint[];
    bestAction: AIAction | null;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a prepared card can be activated as a direct action
 */
function canActivateAsDirectAction(
    state: GameState,
    player: Player,
    pc: PreparedCard,
    rotation: number
): boolean {
    // Check if locked
    if (pc.lockedState && state.round - pc.lockedState.appliedRound === 0) {
        return false;
    }

    // Check essence cost
    if (pc.card.essenceCost) {
        const cost = getEffectiveEssenceCostForPlayer(pc.card, player);
        if (player.essence < cost) return false;
    }

    // Check if silenced
    const isSilenced = player.effectCards.some(
        c => c.type === CardType.SealOfSilence && c.appliedRound === state.round
    );
    if (isSilenced) {
        const isPinkCard = !pc.card.essenceCost && pc.card.color === CardColor.Pink;
        const isSpell = !!pc.card.essenceCost && pc.card.subType === 'Spell';
        if (isPinkCard || isSpell) return false;
    }

    const effect = getCardEffect({ ...pc, rotation });

    // Shield effects can only be used as reactions (unless extended shield)
    const shieldTypes = [
        CardType.Defense, CardType.Hibernation, CardType.Pushback,
        CardType.Dispel, CardType.PsyBlock, CardType.Frost,
        CardType.Invincible, CardType.Counterblast, CardType.DoubleCross
    ];

    if (shieldTypes.includes(effect.type)) {
        // Extended shields (Invincible, SealOfSilence) can be played directly
        if (pc.card.extendedShield) return true;
        // Protector can be played directly in ATTACK mode
        if (effect.type === CardType.Protector && rotation === 180) return true;
        return false;
    }

    return true;
}

/**
 * Check if a prepared card can be used as a counter/reaction
 */
function canActivateAsReaction(
    state: GameState,
    player: Player,
    pc: PreparedCard,
    rotation: number
): boolean {
    // Check if locked
    if (pc.lockedState && state.round - pc.lockedState.appliedRound === 0) {
        return false;
    }

    // Check essence cost
    if (pc.card.essenceCost) {
        const cost = getEffectiveEssenceCostForPlayer(pc.card, player);
        if (player.essence < cost) return false;
    }

    // Check if silenced
    const isSilenced = player.effectCards.some(
        c => c.type === CardType.SealOfSilence && c.appliedRound === state.round
    );
    if (isSilenced) {
        const isPinkCard = !pc.card.essenceCost && pc.card.color === CardColor.Pink;
        const isSpell = !!pc.card.essenceCost && pc.card.subType === 'Spell';
        if (isPinkCard || isSpell) return false;
    }

    const effect = getCardEffect({ ...pc, rotation });

    // DoubleCross only works if:
    // 1. WE initiated the chain (root activation)
    // 2. We haven't passed our active turn yet
    if (effect.type === CardType.DoubleCross) {
        const rootActivation = state.activations[0];
        const isInitiator = rootActivation?.initiatorId === player.id;
        const hasPassed = player.hasPassedActiveTurn === true;
        if (!isInitiator || hasPassed) return false;
    }

    // Only shield/counter effects can be reactions
    const reactionTypes = [
        CardType.Defense, CardType.Hibernation, CardType.Pushback,
        CardType.Dispel, CardType.PsyBlock,
        CardType.Invincible, CardType.Counterblast, CardType.DoubleCross,
        CardType.Protector, CardType.SealOfSilence, CardType.PlantedBomb
    ];

    return reactionTypes.includes(effect.type);
}

/**
 * Check if a card is a legitimate counter for a specific target based on strict hint rules.
 */
function isLegitCounterForHint(
    counterPC: PreparedCard,
    targetPC: PreparedCard
): boolean {
    const counterEffect = getCardEffect(counterPC);
    const targetEffect = getCardEffect(targetPC);
    const targetSubType = targetPC.card.subType;

    if (counterEffect.type === CardType.Counterblast) {
        return canCounterblastTarget(targetPC, counterPC.card.ownerId ?? 0);
    }

    // Hibernation: Only allowed against damage-dealing cards
    if (counterEffect.type === CardType.Hibernation) {
        const delayedDamage = targetEffect.type === CardType.Disease || targetEffect.type === CardType.NaturalGrip;
        return isImmediateOrReflectedDamageSource(targetPC) || delayedDamage || isSpikesCard(targetPC);
    }

    // Pushback: Legitimate against Armory (Stash) or any colored card
    if (counterEffect.type === CardType.Pushback) {
        if (targetSubType === 'Armory') return true;
        if (!targetPC.card.essenceCost) return true; // Valid against all colored cards
    }

    // Psy-Block: Legitimate against Artifact (Stash) or any colored card
    if (counterEffect.type === CardType.PsyBlock) {
        if (targetSubType === 'Artifact') return true;
        if (!targetPC.card.essenceCost) return true; // Valid against all colored cards
    }

    // Dispel: Legitimate against Spell (Stash) or Green/Yellow/Black/Purple cards
    if (counterEffect.type === CardType.Dispel) {
        if (targetSubType === 'Spell') return true;
        if (!targetPC.card.essenceCost) {
            const validColors = [CardColor.Green, CardColor.Yellow, CardColor.Black, CardColor.Purple];
            return validColors.includes(targetPC.card.color);
        }
    }

    // Seal of Silence: Legitimate against Pink color deck or Spell stash
    if (counterEffect.type === CardType.SealOfSilence) {
        const isPink = !targetPC.card.essenceCost && targetPC.card.color === CardColor.Pink;
        const isSpell = !!targetPC.card.essenceCost && targetPC.card.subType === 'Spell';
        return isPink || isSpell;
    }

    // Planted Bomb: Target must be Stash Deck
    if (counterEffect.type === CardType.PlantedBomb) {
        return !!targetPC.card.essenceCost;
    }

    // For other shields (Defense, Invincible, Protector, Counterblast), use broader check
    // These generally counter damage or specific actions.
    const damageDealingTypes = [
        CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
        CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
        CardType.Chromabow
    ];

    if ([CardType.Defense, CardType.Invincible, CardType.Protector].includes(counterEffect.type)) {
        return damageDealingTypes.includes(targetEffect.type);
    }

    // DoubleCross: Counters colored cards (not Stash)
    if (counterEffect.type === CardType.DoubleCross) {
        return !targetPC.card.essenceCost;
    }

    return true; // Default to true for any other specialized interactions
}

/**
 * Identify cards in the activation tree that the player can respond to.
 */
function getRespondableTargets(state: GameState, playerId: number): PreparedCard[] {
    const targets: PreparedCard[] = [];

    // We can respond to any unresolved activation where we didn't play the last card
    for (const act of state.activations) {
        if (act.resolved || act.accepted) continue;

        const lastCard = act.counterChain.length > 0
            ? act.counterChain[act.counterChain.length - 1]
            : act.attackingCard;

        // Can only respond to opponent's cards
        if (lastCard.card.ownerId !== playerId) {
            targets.push(lastCard);
        }
    }

    return targets;
}

/**
 * Check if a card in hand can be prepared
 */
function canPrepareCard(
    state: GameState,
    player: Player,
    cardId: string
): boolean {
    const availableSlots = getAvailablePrepareSlots(state, player.id);
    if (availableSlots <= 0) return false;

    const card = player.hand.find(c => c.id === cardId);
    if (!card) return false;

    return true;
}

/**
 * Check if Mage can cast spell from hand
 */
function canCastSpellFromHand(
    state: GameState,
    player: Player,
    cardId: string
): boolean {
    if (player.playerClass !== 'MAGE') return false;

    const card = player.hand.find(c => c.id === cardId);
    if (!card || card.subType !== 'Spell' || !card.essenceCost) return false;

    const cost = getEffectiveEssenceCostForPlayer(card, player);
    if (player.essence < cost) return false;

    // Check if silenced
    const isSilenced = player.effectCards.some(
        c => c.type === CardType.SealOfSilence && c.appliedRound === state.round
    );
    if (isSilenced) return false;

    return true;
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Get all valid action hints for the current player
 */
export function getValidActionHints(
    state: GameState,
    playerId: number
): HintProviderResult {
    const hints: ActionHint[] = [];

    const player = state.players.find(p => p.id === playerId);
    if (!player) return { hints: [], bestAction: null };

    // Get AI's best recommended action
    let bestAction: AIAction | null = null;
    try {
        if (state.phase === GamePhase.Preparation) {
            const { sequence } = runAIPreparationTurn(state, playerId);
            if (sequence.length > 0) {
                bestAction = sequence[0].action as AIAction;
            } else {
                bestAction = { type: 'PASS', priority: 0 };
            }
        } else if (state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase) {
            bestAction = getBestAIAction(state, playerId);
        } else {
            const phaseAction = getAIPhaseAction(state, playerId);
            if (phaseAction) bestAction = phaseAction;
        }
    } catch (e) {
        console.warn('[aiHintProvider] Failed to get AI best action:', e);
    }

    const bestCardId = (bestAction as any)?.cardId;
    const bestActionType = bestAction?.type;

    // Check if there's an active activation requiring response
    const hasActiveActivation = state.activations.some(act => !act.resolved && !act.accepted);
    const isMyTurnToRespond = hasActiveActivation &&
        state.lastCardPlayerId !== undefined &&
        state.lastCardPlayerId !== playerId;
    const isMyTurnToAct = state.turnPlayerId === playerId;

    // =========================================================================
    // PREPARATION PHASE HINTS
    // =========================================================================
    if (state.phase === GamePhase.Preparation) {
        // Hint for cards that can be prepared
        for (const card of player.hand) {
            if (canPrepareCard(state, player, card.id)) {
                const isAIBest = bestActionType === 'PREPARE_CARD' && bestCardId === card.id;
                hints.push({
                    elementId: card.id,
                    elementType: 'HAND_CARD',
                    priority: isAIBest ? 'AI_BEST' : 'VALID',
                    cardColor: isAIBest ? card.color : undefined,
                    actionType: 'PREPARE_CARD'
                });
            }
        }

        // Empty Slot Hint (if cards can be prepared)
        const availableSlots = getAvailablePrepareSlots(state, player.id);
        const canPrepareAny = player.hand.some(card => canPrepareCard(state, player, card.id));

        if (availableSlots > 0 && canPrepareAny) {
            hints.push({
                elementId: 'prepared-slot-empty',
                elementType: 'SLOT',
                priority: 'VALID',
                actionType: 'PREPARE_CARD'
            });
        }

        // End Day button hint
        if (player.prepared.length > 0) {
            hints.push({
                elementId: 'end-day-button',
                elementType: 'BUTTON',
                priority: bestActionType === 'PASS' ? 'AI_BEST' : 'VALID',
                actionType: 'END_DAY'
            });
        }
    }

    // =========================================================================
    // COMBAT PHASE HINTS
    // =========================================================================
    if (state.phase === GamePhase.Combat || state.phase === GamePhase.TowerPurchase) {
        // Direct activation hints (when it's player's turn and no active chain)
        if (isMyTurnToAct && !hasActiveActivation) {
            for (const pc of player.prepared) {
                const validSides = { top: false, bottom: false };

                // Check top side (rotation 0)
                if (canActivateAsDirectAction(state, player, pc, 0)) {
                    validSides.top = true;
                }

                // Check bottom side (rotation 180) if card has typeB
                if (pc.card.typeB && canActivateAsDirectAction(state, player, pc, 180)) {
                    validSides.bottom = true;
                }

                if (validSides.top || validSides.bottom) {
                    // FILTER: If passed, only show hints for cards playable after pass
                    if (player.hasPassedActiveTurn) {
                        const isPlayable = (pc.card.playableAfterPass) ||
                            (validSides.top && [CardType.Defense, CardType.Hibernation, CardType.Pushback, CardType.Dispel, CardType.PsyBlock, CardType.Invincible, CardType.Counterblast, CardType.DoubleCross, CardType.Protector, CardType.SealOfSilence, CardType.PlantedBomb].includes(getCardEffect({ ...pc, rotation: 0 }).type)) ||
                            (validSides.bottom && pc.card.typeB && [CardType.Defense, CardType.Hibernation, CardType.Pushback, CardType.Dispel, CardType.PsyBlock, CardType.Invincible, CardType.Counterblast, CardType.DoubleCross, CardType.Protector, CardType.SealOfSilence, CardType.PlantedBomb].includes(getCardEffect({ ...pc, rotation: 180 }).type));

                        if (!isPlayable) continue;
                    }

                    const isAIBest = bestActionType === 'ATTEMPT_ACTIVATION' && bestCardId === pc.card.id;
                    hints.push({
                        elementId: pc.card.id,
                        elementType: 'PREPARED_CARD',
                        priority: isAIBest ? 'AI_BEST' : 'VALID',
                        cardColor: isAIBest ? pc.card.color : undefined,
                        validSides,
                        actionType: 'ATTEMPT_ACTIVATION',
                        bestActionRotation: isAIBest ? (bestAction as any)?.rotation : undefined,
                        secondaryElementId: isAIBest ? (
                            (bestAction as any)?.targetId ? `avatar-${(bestAction as any).targetId}` : 
                            (bestAction as any)?.targetActivationId ? (bestAction as any).targetActivationId : 
                            undefined
                        ) : undefined
                    });
                }
            }

            // Essence burning actions (if already in essence mode, otherwise button is highlighted below)
            const discardMode = state.discardMode?.[playerId];
            if (discardMode === 'night' || discardMode === 'day') {
                for (const pc of player.prepared) {
                    if (pc.card.essenceCost && (!pc.lockedState || state.round - pc.lockedState.appliedRound !== 0)) {
                        const isAIBest = bestActionType === 'DISCARD_STASH' && bestCardId === pc.card.id;
                        hints.push({
                            elementId: pc.card.id,
                            elementType: 'PREPARED_CARD',
                            priority: isAIBest ? 'AI_BEST' : 'VALID',
                            actionType: 'DISCARD_STASH'
                        });
                    }
                }
                for (const c of player.hand) {
                    if (!c.essenceCost) {
                        const isAIBest = bestActionType === 'DISCARD_HAND_FOR_ESSENCE' && bestCardId === c.id;
                        hints.push({
                            elementId: c.id,
                            elementType: 'HAND_CARD',
                            priority: isAIBest ? 'AI_BEST' : 'VALID',
                            actionType: 'DISCARD_HAND_FOR_ESSENCE'
                        });
                    }
                }
            }

            // Pass button hint
            hints.push({
                elementId: 'pass-button',
                elementType: 'BUTTON',
                priority: bestActionType === 'PASS' ? 'AI_BEST' : 'VALID',
                actionType: 'PASS'
            });

            // Tower purchase hint
            if (state.phase === GamePhase.TowerPurchase ||
                (state.phase === GamePhase.Combat && player.towers.length < 5)) {
                for (const color of state.availableTowers) {
                    const cardsOfColor = player.hand.filter(c => c.color === color && !c.essenceCost).length;
                    if (cardsOfColor >= 3) {
                        const isAIBest = bestActionType === 'PURCHASE_TOWER' && (bestAction as any)?.color === color;
                        hints.push({
                            elementId: `tower-${color}`,
                            elementType: 'TOWER',
                            priority: isAIBest ? 'AI_BEST' : 'VALID',
                            cardColor: color,
                            actionType: 'PURCHASE_TOWER'
                        });
                    }
                }
            }
        }

        // Reaction hints (when responding to opponent's action)
        if (isMyTurnToRespond) {
            const respondableTargets = getRespondableTargets(state, playerId);

            for (const pc of player.prepared) {
                const validSides = { top: false, bottom: false };

                // Check top side (rotation 0)
                if (canActivateAsReaction(state, player, pc, 0)) {
                    // Refinement: Only valid if it legit counters at least one target
                    if (respondableTargets.some(target => isLegitCounterForHint({ ...pc, rotation: 0 }, target))) {
                        validSides.top = true;
                    }
                }

                // Check bottom side (rotation 180) if card has typeB
                if (pc.card.typeB && canActivateAsReaction(state, player, pc, 180)) {
                    // Refinement: Only valid if it legit counters at least one target
                    if (respondableTargets.some(target => isLegitCounterForHint({ ...pc, rotation: 180 }, target))) {
                        validSides.bottom = true;
                    }
                }

                if (validSides.top || validSides.bottom) {
                    // FILTER: If passed, only show hints for cards playable after pass
                    if (player.hasPassedActiveTurn) {
                        const isPlayable = (pc.card.playableAfterPass) ||
                            (validSides.top && [CardType.Defense, CardType.Hibernation, CardType.Pushback, CardType.Dispel, CardType.PsyBlock, CardType.Invincible, CardType.Counterblast, CardType.DoubleCross, CardType.Protector, CardType.SealOfSilence, CardType.PlantedBomb].includes(getCardEffect({ ...pc, rotation: 0 }).type)) ||
                            (validSides.bottom && pc.card.typeB && [CardType.Defense, CardType.Hibernation, CardType.Pushback, CardType.Dispel, CardType.PsyBlock, CardType.Invincible, CardType.Counterblast, CardType.DoubleCross, CardType.Protector, CardType.SealOfSilence, CardType.PlantedBomb].includes(getCardEffect({ ...pc, rotation: 180 }).type));

                        if (!isPlayable) continue;
                    }

                    const isAIBest = bestActionType === 'COUNTER_ACTIVATION' && bestCardId === pc.card.id;
                    hints.push({
                        elementId: pc.card.id,
                        elementType: 'PREPARED_CARD',
                        priority: isAIBest ? 'AI_BEST' : 'VALID',
                        cardColor: isAIBest ? pc.card.color : undefined,
                        validSides,
                        actionType: 'COUNTER_ACTIVATION',
                        bestActionRotation: isAIBest ? (bestAction as any)?.rotation : undefined,
                        secondaryElementId: isAIBest ? (bestAction as any)?.targetActivationId : undefined
                    });
                }
            }

            // Accept button hint
            hints.push({
                elementId: 'accept-button',
                elementType: 'BUTTON',
                priority: bestActionType === 'ACCEPT_ACTIVATION' ? 'AI_BEST' : 'VALID',
                actionType: 'ACCEPT_ACTIVATION'
            });
        }

        // Mage spell hints (can cast from hand)
        if (player.playerClass === 'MAGE' && (isMyTurnToAct || isMyTurnToRespond)) {
            let hasAnyCastableSpell = false;
            const respondableTargets = isMyTurnToRespond ? getRespondableTargets(state, playerId) : [];

            for (const card of player.hand) {
                if (canCastSpellFromHand(state, player, card.id)) {
                    // Refinement: If it's response turn, only show if it legit counters something
                    if (isMyTurnToRespond) {
                        if (!respondableTargets.some(target => isLegitCounterForHint({ card, rotation: 0 }, target))) {
                            continue;
                        }
                    }

                    hasAnyCastableSpell = true;
                    const isAIBest = bestActionType === 'MAGE_CAST_SPELL' && bestCardId === card.id;
                    hints.push({
                        elementId: card.id,
                        elementType: 'SPELL',
                        priority: isAIBest ? 'AI_BEST' : 'VALID',
                        cardColor: isAIBest ? card.color : undefined,
                        actionType: 'MAGE_CAST_SPELL',
                        secondaryElementId: isAIBest ? (
                            (bestAction as any)?.targetId ? `avatar-${(bestAction as any).targetId}` : 
                            (bestAction as any)?.targetActivationId ? (bestAction as any).targetActivationId : 
                            undefined
                        ) : undefined
                    });
                }
            }

            // Hint for Spells button if any spells can be cast
            if (hasAnyCastableSpell) {
                hints.push({
                    elementId: 'mage-spells-button',
                    elementType: 'BUTTON',
                    priority: 'VALID',
                    actionType: 'OPEN_MAGE_SPELLS'
                });
            }
        }

        // Essence Mode hint
        const hasStashPrepared = player.prepared.some(pc => !!pc.card.subType);
        const playerCanBurnHand = player.hand.some(c => !c.essenceCost);
        const canUseEssence = (isMyTurnToAct && !hasActiveActivation) || isMyTurnToRespond;

        if (state.phase === GamePhase.Combat && canUseEssence && (hasStashPrepared || playerCanBurnHand)) {
            const discardMode = state.discardMode?.[playerId];
            const needsEssenceMode = !discardMode && (bestActionType === 'DISCARD_STASH' || bestActionType === 'DISCARD_HAND_FOR_ESSENCE');
            const needsEssenceModeOff = discardMode && bestActionType !== 'DISCARD_STASH' && bestActionType !== 'DISCARD_HAND_FOR_ESSENCE' && bestActionType !== 'PASS';
            
            if (needsEssenceMode || needsEssenceModeOff) {
                hints.unshift({
                    elementId: 'essence-mode-button',
                    elementType: 'BUTTON',
                    priority: 'AI_BEST',
                    actionType: needsEssenceMode ? 'TOGGLE_ESSENCE_MODE' : 'TOGGLE_ESSENCE_MODE_OFF'
                });
            }
        }

        // Astrid Blast hint (requires no active activation chain)
        if (bestActionType === 'ASTRID_BLAST' && !hasActiveActivation) {
            const targetId = (bestAction as any)?.targetId;
            hints.push({
                elementId: 'avatar-self', // Spotlight the player's own avatar as the source of the blast
                elementType: 'AVATAR',
                priority: 'AI_BEST',
                actionType: 'ASTRID_BLAST',
                secondaryElementId: targetId ? `avatar-${targetId}` : undefined
            });
        }

        // Capture Tower button hint
        if (isMyTurnToAct && !hasActiveActivation && !player.capturedTowerThisTurn) {
            const canCaptureAny = state.availableTowers.some(color => {
                const cardsOfColor = player.hand.filter(c => c.color === color && !c.essenceCost).length;
                return cardsOfColor >= 3;
            });
            if (canCaptureAny) {
                hints.push({
                    elementId: 'capture-tower-button',
                    elementType: 'BUTTON',
                    priority: 'VALID',
                    actionType: 'START_TOWER_PURCHASE'
                });
            }
        }
    }

    // =========================================================================
    // SPECIAL PHASES HINTS
    // =========================================================================
    if (state.phase === GamePhase.LockedCardOverflow) {
        if (state.overflowPlayerId === playerId) {
            const lockedCards = player.prepared.filter(pc => pc.lockedState);
            lockedCards.forEach(pc => {
                const isAIBest = bestActionType === 'CHOOSE_LOCKED_CARD_RETURN' && bestCardId === pc.card.id;
                hints.push({
                    elementId: pc.card.id,
                    elementType: 'PREPARED_CARD',
                    priority: isAIBest ? 'AI_BEST' : 'VALID',
                    cardColor: isAIBest ? pc.card.color : undefined,
                    actionType: 'CHOOSE_LOCKED_CARD_RETURN'
                });
            });
        }
    }

    if (state.phase === GamePhase.AWAITING_BURGLARY_CHOICE) {
        if (state.burglaryInitiatorId === playerId) {
            const targetPlayer = state.players.find(p => p.id === state.burglaryTargetId);
            if (targetPlayer) {
                targetPlayer.hand.forEach(card => {
                    const isAIBest = bestActionType === 'CHOOSE_BURGLARY_CARD' && (bestAction as any)?.targetCardId === card.id;
                    hints.push({
                        elementId: card.id,
                        elementType: 'HAND_CARD', // Even though it's opponent's hand, the UI element is the card
                        priority: isAIBest ? 'AI_BEST' : 'VALID',
                        actionType: 'CHOOSE_BURGLARY_CARD'
                    });
                });
            }
        }
    }

    if (state.phase === GamePhase.AWAITING_CHROMABOW_CHOICE) {
        if (state.chromabowInitiatorId === playerId) {
            Object.values(CardColor).forEach(color => {
                if (state.colorDecks[color]?.length > 0) {
                    const isAIBest = bestActionType === 'CHOOSE_CHROMABOW_COLOR' && (bestAction as any)?.color === color;
                    hints.push({
                        elementId: `chromabow-color-${color}`,
                        elementType: 'BUTTON',
                        priority: isAIBest ? 'AI_BEST' : 'VALID',
                        actionType: 'CHOOSE_CHROMABOW_COLOR'
                    });
                }
            });
        }
    }

    if (state.phase === GamePhase.AWAITING_HIBERNATION_CHOICE) {
        if (state.hibernationInitiatorId === playerId) {
            Object.values(CardColor).forEach(color => {
                if (state.colorDecks[color]?.length > 0) {
                    const isAIBest = bestActionType === 'CHOOSE_HIBERNATION_COLOR' && (bestAction as any)?.color === color;
                    hints.push({
                        elementId: `hibernation-color-${color}`,
                        elementType: 'BUTTON',
                        priority: isAIBest ? 'AI_BEST' : 'VALID',
                        actionType: 'CHOOSE_HIBERNATION_COLOR'
                    });
                }
            });
        }
    }

    if (state.phase === GamePhase.AWAITING_PURIFICATION_TARGET) {
        if (state.purificationInitiatorId === playerId) {
            const isAIBest = bestActionType === 'CHOOSE_PURIFICATION_TARGET';
            const targetColor = (bestAction as any)?.targetColor;
            
            // In UI, purification target is selected by clicking a tower
            if (isAIBest && targetColor) {
                 hints.push({
                    elementId: `tower-${targetColor}`,
                    elementType: 'TOWER',
                    priority: 'AI_BEST',
                    actionType: 'CHOOSE_PURIFICATION_TARGET'
                });
            }
        }
    }

    return { hints, bestAction };
}

/**
 * Get hint for a specific element by ID
 */
export function getHintForElement(
    hints: ActionHint[],
    elementId: string
): ActionHint | undefined {
    return hints.find(h => h.elementId === elementId);
}

/**
 * Check if any hints are available
 */
export function hasAnyHints(hints: ActionHint[]): boolean {
    return hints.length > 0;
}

/**
 * Get the AI's best recommended hint
 */
export function getBestHint(hints: ActionHint[]): ActionHint | undefined {
    return hints.find(h => h.priority === 'AI_BEST');
}
